/**
 * ProductGallery
 *
 * - Desktop (hover: hover, pointer: fine): hover over the main image to zoom.
 *   A magnifier lens and a zoomed background layer track the cursor position.
 * - Mobile / touch: no zoom. Tap a thumbnail to swap the featured image.
 * - Both: clicking a thumbnail highlights it and updates the main image src.
 *
 * Required markup:
 *   [data-product-gallery]              — component root
 *   [data-gallery-main]                 — wrapper div for the main image
 *   [data-gallery-main-img]             — the <img> inside the main wrapper
 *   [data-gallery-zoom-layer]           — absolutely-positioned zoom overlay div
 *   [data-gallery-zoom-lens]            — magnifier circle div
 *   [data-gallery-thumb]                — each thumbnail button
 *     data-main-src  — URL to load at 1200w
 *     data-zoom-src  — URL to load at 2000w (zoom background)
 *     data-alt       — alt text for the main image
 */
class ProductGallery {
  constructor(root) {
    this.root        = root;
    this.mainWrapper = root.querySelector('[data-gallery-main]');
    this.mainImg     = root.querySelector('[data-gallery-main-img]');
    this.zoomLayer   = root.querySelector('[data-gallery-zoom-layer]');
    this.lens        = root.querySelector('[data-gallery-zoom-lens]');
    this.thumbs      = Array.from(root.querySelectorAll('[data-gallery-thumb]'));

    if (!this.mainWrapper || !this.mainImg || !this.zoomLayer || !this.lens) return;

    this._bindThumbs();

    // Only wire up zoom on devices that have a true hover capability.
    // matchMedia is evaluated once at init; if a user switches pointer
    // device mid-session they'd need to reload — acceptable trade-off.
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (canHover) this._bindZoom();
  }

  /* ── Thumbnail switching ──────────────────────────────── */

  _bindThumbs() {
    this.thumbs.forEach((btn) => {
      btn.addEventListener('click', () => this._activateThumb(btn));

      // Support keyboard: Enter / Space already fire click, but
      // make sure arrow keys cycle through the strip.
      btn.addEventListener('keydown', (e) => {
        const idx = this.thumbs.indexOf(btn);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          const next = this.thumbs[idx + 1] || this.thumbs[0];
          next.focus();
          this._activateThumb(next);
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = this.thumbs[idx - 1] || this.thumbs[this.thumbs.length - 1];
          prev.focus();
          this._activateThumb(prev);
        }
      });
    });

    // Prime the zoom layer with the first (already-active) image.
    const initial = this.thumbs.find((t) => t.getAttribute('aria-current') === 'true') ?? this.thumbs[0];
    if (initial?.dataset.zoomSrc) this._setZoomSrc(initial.dataset.zoomSrc);
  }

  _activateThumb(btn) {
    const src  = btn.dataset.mainSrc;
    const zoom = btn.dataset.zoomSrc;
    const alt  = btn.dataset.alt || '';

    if (src) {
      // Clear srcset/sizes so the browser uses our explicit src.
      this.mainImg.srcset = '';
      this.mainImg.sizes  = '';
      this.mainImg.src    = src;
    }

    if (alt) this.mainImg.alt = alt;
    if (zoom) this._setZoomSrc(zoom);

    this.thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
    btn.setAttribute('aria-current', 'true');
  }

  /* ── Desktop zoom ────────────────────────────────────── */

  _bindZoom() {
    // Track cursor position over the main image wrapper.
    this.mainWrapper.addEventListener('mousemove', (e) => this._onZoomMove(e));
    this.mainWrapper.addEventListener('mouseleave', () => this._hideZoomLens());
  }

  _setZoomSrc(url) {
    const bg = `url("${url}")`;
    this.zoomLayer.style.backgroundImage = bg;
    this.lens.style.backgroundImage      = bg;
    this.lens.style.backgroundRepeat     = 'no-repeat';
    this.lens.style.backgroundSize       = '200% 200%';
  }

  _onZoomMove(e) {
    const rect = this.mainImg.getBoundingClientRect();

    // Clamp cursor inside the image bounds.
    const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(e.clientY - rect.top,  0), rect.height);

    const px = (x / rect.width)  * 100;
    const py = (y / rect.height) * 100;

    // Position the zoomed background.
    const bgPos = `${px}% ${py}%`;
    this.zoomLayer.style.backgroundPosition = bgPos;

    // Move the lens circle to follow the cursor.
    this.lens.style.left                     = `${x}px`;
    this.lens.style.top                      = `${y}px`;
    this.lens.style.backgroundPosition       = bgPos;
  }

  _hideZoomLens() {
    // Push the lens off-screen; CSS transition fades the opacity.
    this.lens.style.left = '-999px';
    this.lens.style.top  = '-999px';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-gallery]').forEach((el) => new ProductGallery(el));
});
